/**
 * Authentication Context - SeeTex Web
 * 
 * SECURITY FEATURES:
 * - Secure session management with Firebase Auth
 * - Automatic token refresh handling
 * - Protected user state
 * - CSRF-safe operations (Firebase handles this internally)
 * - Secure error handling (no sensitive data leakage)
 * - Rate limiting awareness
 */

import {
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    type ReactNode,
} from "react";
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    GoogleAuthProvider,
    EmailAuthProvider,
    linkWithCredential,
    signInAnonymously,
    signOut,
    sendPasswordResetEmail,
    sendEmailVerification,
    updateProfile,
    type User,
    type AuthError,
} from "firebase/auth";
import {
    getFirebaseAuth,
    isDemoMode,
    sanitizeEmail,
    isValidEmail,
    validatePassword,
} from "../config/firebase";

// Error codes that are safe to show to users
const USER_FRIENDLY_ERRORS: Record<string, string> = {
    "auth/email-already-in-use": "An account with this email already exists",
    "auth/invalid-email": "Please enter a valid email address",
    "auth/operation-not-allowed": "This sign-in method is not enabled",
    "auth/weak-password": "Please choose a stronger password",
    "auth/user-disabled": "This account has been disabled",
    "auth/user-not-found": "Invalid email or password",
    "auth/wrong-password": "Invalid email or password",
    "auth/invalid-credential": "Invalid email or password",
    "auth/too-many-requests": "Too many attempts. Please try again later",
    "auth/network-request-failed": "Network error. Please check your connection",
    "auth/popup-closed-by-user": "Sign-in was cancelled",
    "auth/popup-blocked": "Please allow popups for this site",
};

// Get user-friendly error message (never expose internal details)
function getAuthErrorMessage(error: AuthError | { code?: string }): string {
    const code = (error as AuthError).code;
    return (code && USER_FRIENDLY_ERRORS[code]) || "An error occurred. Please try again.";
}

// Auth state interface
interface AuthState {
    user: User | null;
    loading: boolean;
    error: string | null;
}

// Auth context value interface
interface AuthContextValue extends AuthState {
    // Auth actions
    signUp: (email: string, password: string, displayName?: string) => Promise<void>;
    signIn: (email: string, password: string) => Promise<void>;
    signInWithGoogle: () => Promise<void>;
    continueAsGuest: () => Promise<void>;
    linkGuest: (email: string, password: string, displayName?: string) => Promise<void>;
    logout: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    resendVerificationEmail: () => Promise<void>;

    // State helpers
    clearError: () => void;
    isAuthenticated: boolean;
    isGuest: boolean;
    isEmailVerified: boolean;
    isDemoMode: boolean;
}

// Demo user for development without Firebase
const DEMO_USER: Partial<User> = {
    uid: "demo-user-123",
    email: "demo@seetex.app",
    displayName: "Demo User",
    emailVerified: true,
};

const AuthContext = createContext<AuthContextValue | null>(null);

// Google Auth Provider with security scopes
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope("email");
googleProvider.addScope("profile");
// Force account selection for security
googleProvider.setCustomParameters({
    prompt: "select_account",
});

export function AuthProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<AuthState>({
        user: null,
        loading: true,
        error: null,
    });

    const demoMode = isDemoMode();

    // Listen to auth state changes
    useEffect(() => {
        if (demoMode) {
            // In demo mode, simulate logged out state initially
            setState({ user: null, loading: false, error: null });
            return;
        }

        const auth = getFirebaseAuth();

        // Complete any pending redirect sign-in (from signInWithRedirect fallback)
        getRedirectResult(auth).catch((err) => {
            const authErr = err as AuthError;
            if (authErr.code) {
                console.error("[SeeTex Auth] Redirect result error:", authErr.code);
                setState((prev) => ({ ...prev, loading: false, error: getAuthErrorMessage(authErr) }));
            }
        });

        const unsubscribe = onAuthStateChanged(
            auth,
            (user) => {
                setState({ user, loading: false, error: null });
            },
            (error) => {
                const authErr = error as AuthError;
                console.error("[SeeTex Auth] Auth state error:", authErr.code);
                setState({ user: null, loading: false, error: getAuthErrorMessage(authErr) });
            }
        );

        return () => unsubscribe();
    }, [demoMode]);

    // Clear error state
    const clearError = useCallback(() => {
        setState((prev) => ({ ...prev, error: null }));
    }, []);

    // Sign up with email/password
    const signUp = useCallback(
        async (email: string, password: string, displayName?: string) => {
            if (demoMode) {
                setState({ user: DEMO_USER as User, loading: false, error: null });
                return;
            }

            // Input validation (defense in depth)
            const sanitizedEmail = sanitizeEmail(email);
            if (!isValidEmail(sanitizedEmail)) {
                setState((prev) => ({ ...prev, error: "Please enter a valid email address" }));
                return;
            }

            const passwordValidation = validatePassword(password);
            if (!passwordValidation.valid) {
                setState((prev) => ({ ...prev, error: passwordValidation.errors[0] }));
                return;
            }

            setState((prev) => ({ ...prev, loading: true, error: null }));

            try {
                const auth = getFirebaseAuth();
                const userCredential = await createUserWithEmailAndPassword(
                    auth,
                    sanitizedEmail,
                    password
                );

                // Update display name if provided
                if (displayName && userCredential.user) {
                    await updateProfile(userCredential.user, { displayName });
                }

                // Send email verification for security
                if (userCredential.user) {
                    await sendEmailVerification(userCredential.user);
                }

                setState({ user: userCredential.user, loading: false, error: null });
            } catch (error) {
                const authError = error as AuthError;
                console.error("[SeeTex Auth] Sign up error:", authError.code);
                setState((prev) => ({
                    ...prev,
                    loading: false,
                    error: getAuthErrorMessage(authError),
                }));
            }
        },
        [demoMode]
    );

    // Sign in with email/password
    const signIn = useCallback(
        async (email: string, password: string) => {
            if (demoMode) {
                setState({ user: DEMO_USER as User, loading: false, error: null });
                return;
            }

            // Input validation
            const sanitizedEmail = sanitizeEmail(email);
            if (!isValidEmail(sanitizedEmail)) {
                setState((prev) => ({ ...prev, error: "Please enter a valid email address" }));
                return;
            }

            if (!password) {
                setState((prev) => ({ ...prev, error: "Please enter your password" }));
                return;
            }

            setState((prev) => ({ ...prev, loading: true, error: null }));

            try {
                const auth = getFirebaseAuth();
                const userCredential = await signInWithEmailAndPassword(
                    auth,
                    sanitizedEmail,
                    password
                );
                setState({ user: userCredential.user, loading: false, error: null });
            } catch (error) {
                const authError = error as AuthError;
                console.error("[SeeTex Auth] Sign in error:", authError.code);
                setState((prev) => ({
                    ...prev,
                    loading: false,
                    error: getAuthErrorMessage(authError),
                }));
            }
        },
        [demoMode]
    );

    // Sign in with Google
    const signInWithGoogle = useCallback(async () => {
        if (demoMode) {
            setState({ user: DEMO_USER as User, loading: false, error: null });
            return;
        }

        setState((prev) => ({ ...prev, loading: true, error: null }));

        try {
            const auth = getFirebaseAuth();
            const result = await signInWithPopup(auth, googleProvider);
            setState({ user: result.user, loading: false, error: null });
        } catch (error) {
            const authError = error as AuthError;
            // If popup was blocked, fall back to redirect flow
            if (authError.code === "auth/popup-blocked" || authError.code === "auth/popup-closed-by-user") {
                try {
                    const auth = getFirebaseAuth();
                    await signInWithRedirect(auth, googleProvider);
                    return; // Page will redirect — no further state update needed
                } catch (redirectError) {
                    const redirErr = redirectError as AuthError;
                    console.error("[SeeTex Auth] Redirect fallback error:", redirErr.code);
                    setState((prev) => ({
                        ...prev,
                        loading: false,
                        error: getAuthErrorMessage(redirErr),
                    }));
                    return;
                }
            }
            console.error("[SeeTex Auth] Google sign in error:", authError.code);
            setState((prev) => ({
                ...prev,
                loading: false,
                error: getAuthErrorMessage(authError),
            }));
        }
    }, [demoMode]);

    // Continue as guest (anonymous auth)
    const continueAsGuest = useCallback(async () => {
        if (demoMode) {
            setState({ user: { ...DEMO_USER, isAnonymous: true } as User, loading: false, error: null });
            return;
        }

        setState((prev) => ({ ...prev, loading: true, error: null }));

        try {
            const auth = getFirebaseAuth();
            const result = await signInAnonymously(auth);
            setState({ user: result.user, loading: false, error: null });
        } catch (error) {
            const authError = error as AuthError;
            console.error("[SeeTex Auth] Anonymous sign in error:", authError.code);
            setState((prev) => ({
                ...prev,
                loading: false,
                error: getAuthErrorMessage(authError),
            }));
        }
    }, [demoMode]);

    // Link a guest account to email/password credentials
    const linkGuest = useCallback(
        async (email: string, password: string, displayName?: string) => {
            if (demoMode || !state.user?.isAnonymous) return;

            const sanitizedEmail = sanitizeEmail(email);
            if (!isValidEmail(sanitizedEmail)) {
                setState((prev) => ({ ...prev, error: "Please enter a valid email address" }));
                return;
            }

            const passwordValidation = validatePassword(password);
            if (!passwordValidation.valid) {
                setState((prev) => ({ ...prev, error: passwordValidation.errors[0] }));
                return;
            }

            setState((prev) => ({ ...prev, loading: true, error: null }));

            try {
                const credential = EmailAuthProvider.credential(sanitizedEmail, password);
                const result = await linkWithCredential(state.user, credential);

                if (displayName && result.user) {
                    await updateProfile(result.user, { displayName });
                }
                if (result.user) {
                    await sendEmailVerification(result.user);
                }

                setState({ user: result.user, loading: false, error: null });
            } catch (error) {
                const authError = error as AuthError;
                console.error("[SeeTex Auth] Link guest error:", authError.code);
                setState((prev) => ({
                    ...prev,
                    loading: false,
                    error: getAuthErrorMessage(authError),
                }));
            }
        },
        [demoMode, state.user]
    );

    // Sign out
    const logout = useCallback(async () => {
        if (demoMode) {
            setState({ user: null, loading: false, error: null });
            return;
        }

        setState((prev) => ({ ...prev, loading: true, error: null }));

        try {
            const auth = getFirebaseAuth();
            await signOut(auth);
            setState({ user: null, loading: false, error: null });
        } catch (error) {
            const authError = error as AuthError;
            console.error("[SeeTex Auth] Sign out error:", authError.code);
            setState((prev) => ({
                ...prev,
                loading: false,
                error: getAuthErrorMessage(authError),
            }));
        }
    }, [demoMode]);

    // Password reset
    const resetPassword = useCallback(
        async (email: string) => {
            if (demoMode) {
                // Simulate success in demo mode
                return;
            }

            const sanitizedEmail = sanitizeEmail(email);
            if (!isValidEmail(sanitizedEmail)) {
                setState((prev) => ({ ...prev, error: "Please enter a valid email address" }));
                return;
            }

            setState((prev) => ({ ...prev, loading: true, error: null }));

            try {
                const auth = getFirebaseAuth();
                await sendPasswordResetEmail(auth, sanitizedEmail);
                // Don't reveal if email exists or not (security)
                setState((prev) => ({ ...prev, loading: false }));
            } catch (error) {
                const authError = error as AuthError;
                console.error("[SeeTex Auth] Password reset error:", authError.code);
                // For security, show same message regardless of whether email exists
                setState((prev) => ({ ...prev, loading: false }));
            }
        },
        [demoMode]
    );

    // Resend verification email
    const resendVerificationEmail = useCallback(async () => {
        if (demoMode || !state.user) return;

        try {
            await sendEmailVerification(state.user);
        } catch (error) {
            const authError = error as AuthError;
            console.error("[SeeTex Auth] Verification email error:", authError.code);
            setState((prev) => ({
                ...prev,
                error: getAuthErrorMessage(authError),
            }));
        }
    }, [demoMode, state.user]);

    const value: AuthContextValue = {
        ...state,
        signUp,
        signIn,
        signInWithGoogle,
        continueAsGuest,
        linkGuest,
        logout,
        resetPassword,
        resendVerificationEmail,
        clearError,
        isAuthenticated: !!state.user,
        isGuest: state.user?.isAnonymous ?? false,
        isEmailVerified: state.user?.emailVerified ?? false,
        isDemoMode: demoMode,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Custom hook to use auth context
export function useAuth(): AuthContextValue {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
